import '@audio-network/componentlibrary/main.css'
import '@audio-network/componentlibrary/theme.css'
import '@audio-network/player/main.css'

import { AnwThemeProvider, LinkProvider } from '@audio-network/componentlibrary'
import { PlayerProvider } from '@audio-network/player'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation, i18n } from 'next-i18next'
import { FC, useEffect, useRef } from 'react'
import { StyleSheetManager } from 'styled-components'

import getRequest from '@/client/utils/getRequest'
import UserSession from '@/components/app/UserSession'
import PrimaryLayout from '@/components/layout/PrimaryLayout/PrimaryLayout'
import Link from '@/components/shared/Link'
import { ClientToastContainer } from '@/components/shared/Toast/ClientToastContainer'
import { useShowCookieControl } from '@/hooks/useShowCookieControl'
import { basePath } from '@/next.config'
import type { AlgoliaSearchKey } from '@/pages/api/musicapp/_services/algolia/searchKey'
import styles from '@/styles/app.module.css'
import getLocaleFromCookie from '@/utils/cookies/getLocaleFromCookie'
import timeAgoLocales from '@/utils/formatting/timeAgoLocales'

import CookieControl from '../components/app/CookieControl/CookieControl'
import { ModalHost } from '../components/app/ModalHost/ModalHost'
import { NewRelicAgent } from '../components/app/NewRelic/NewRelicAgent'
import * as gtm from '../lib/gtm'

type MusicApp = AppProps<{ algoliaSearchKey?: AlgoliaSearchKey }>

const MusicApp = ({ Component, pageProps, router }: MusicApp): JSX.Element => {
	const showCookieControl = useShowCookieControl()
	const algoliaInsightsInitialised = useRef(false)
	const navigationTranslations = i18n?.getResourceBundle(
		i18n.resolvedLanguage,
		'navigation'
	)
	// ON-52: Disable router prefetching to reduce contentful graphQL and image request Server Side for blurred images
	useEffect(() => {
		router.prefetch = () => Promise.resolve()
	}, [router])

	useEffect(() => {
		timeAgoLocales(getLocaleFromCookie())
	}, [])

	useEffect(() => {
		if (!window.aa || algoliaInsightsInitialised.current) return
		// initialise algolia insights once here
		// if a user accepts analytics cookies the library will update the userHasOptedOut flag in cookieControlConfig
		const initAlgoliaInsights = (key: AlgoliaSearchKey): void => {
			if (!window.aa || algoliaInsightsInitialised.current) return
			window.aa('init', {
				appId: key.appId,
				apiKey: key.apiKey,
				userHasOptedOut: true,
			})
			algoliaInsightsInitialised.current = true
		}

		// Search has SSR key
		if (pageProps.algoliaSearchKey) {
			initAlgoliaInsights(pageProps.algoliaSearchKey)
			return
		}

		// Non-search routes fetch a key only for Algolia Insights
		void getRequest<AlgoliaSearchKey>(
			`${basePath}/api/musicapp/algolia/search-key`,
			'Algolia Insights Key'
		).then((key) => {
			if (!key) return
			initAlgoliaInsights(key)
		})
	}, [pageProps.algoliaSearchKey])

	useEffect(() => {
		// the pageview event is already fired for the initial page load outside of the router events. Here we are just adding an event listener to fire a pageview event on route change
		const handleRouteChange = (url): void => {
			gtm.pageview(url)
		}
		router.events.on('routeChangeComplete', handleRouteChange)
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
		}
	}, [router.events])

	return (
		<>
			<Head>
				<meta name="viewport" content="width=device-width, minimum-scale=1.0" />
			</Head>
			<div className={styles.app}>
				<UserSession />
				<StyleSheetManager>
					<AnwThemeProvider>
						<LinkProvider component={Link}>
							<ClientToastContainer />
							<PlayerProvider>
								<PrimaryLayout translations={navigationTranslations}>
									<Component {...pageProps} />
								</PrimaryLayout>
								<ModalHost />
							</PlayerProvider>
							{showCookieControl && <CookieControl />}
						</LinkProvider>
					</AnwThemeProvider>
				</StyleSheetManager>
			</div>
			<NewRelicAgent />
		</>
	)
}

export default appWithTranslation(MusicApp as FC)
